import React from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  VStack,
  HStack,
  Text,
  Box,
  Badge,
  Divider,
  Icon,
  Wrap,
  WrapItem,
  Tag,
  TagLabel
} from '@chakra-ui/react';
import { 
  CalendarIcon, 
  TimeIcon, 
  EditIcon,
  AtSignIcon,
  InfoOutlineIcon
} from '@chakra-ui/icons';
import { MdLocationOn } from 'react-icons/md';
import { FaBuilding } from 'react-icons/fa';

const EventViewModal = ({ isOpen, onClose, event, onEdit, onClientClick }) => {
  if (!event) return null;

  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('pt-BR', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDateOnly = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('pt-BR', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getEventTypeLabel = (type) => {
    const types = {
      appointment: 'Compromisso',
      meeting: 'Reunião',
      task: 'Tarefa',
      reminder: 'Lembrete',
      other: 'Outro'
    };
    return types[type] || type;
  };

  const getEventTypeColor = (type) => {
    const colors = {
      appointment: 'blue',
      meeting: 'purple',
      task: 'green',
      reminder: 'orange',
      other: 'gray'
    };
    return colors[type] || 'gray';
  };

  const getVisibilityLabel = (visibility) => {
    const labels = {
      private: 'Privado',
      team: 'Equipe',
      public: 'Público'
    };
    return labels[visibility] || visibility;
  };

  const getVisibilityColor = (visibility) => {
    const colors = {
      private: 'red',
      team: 'yellow',
      public: 'green'
    };
    return colors[visibility] || 'gray';
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent maxW="container.sm" mx="auto">
        <ModalHeader>
          <HStack spacing={3}>
            <Icon as={CalendarIcon} color="green.500" />
            <Text>{event.title}</Text>
          </HStack>
        </ModalHeader>
        <ModalCloseButton />
        
        <ModalBody>
          <VStack spacing={5} align="stretch">
            {/* Type and Visibility Badges */}
            <HStack spacing={3}>
              <Badge colorScheme={getEventTypeColor(event.type)} fontSize="sm" px={3} py={1}>
                {getEventTypeLabel(event.type)}
              </Badge>
              {event.visibility && (
                <Badge colorScheme={getVisibilityColor(event.visibility)} fontSize="sm" px={3} py={1}>
                  {getVisibilityLabel(event.visibility)}
                </Badge>
              )}
            </HStack>

            {/* Description */}
            {event.description && (
              <Box>
                <HStack mb={2}>
                  <Icon as={InfoOutlineIcon} color="gray.500" />
                  <Text fontWeight="semibold" color="gray.700">Descrição</Text>
                </HStack>
                <Box pl={6}>
                  <Text color="gray.600" whiteSpace="pre-wrap">
                    {event.description}
                  </Text>
                </Box>
              </Box>
            )}

            <Divider />

            {/* Date and Time */}
            <Box>
              <HStack mb={3}>
                <Icon as={TimeIcon} color="green.500" />
                <Text fontWeight="semibold" color="gray.700">Data e Hora</Text>
              </HStack>
              <Box pl={6}>
                {event.allDay ? (
                  <VStack align="start" spacing={1}>
                    <Text color="gray.600">
                      <strong>Dia inteiro</strong>
                    </Text>
                    <Text color="gray.600">
                      Início: {formatDateOnly(event.startDate)}
                    </Text>
                    <Text color="gray.600">
                      Término: {formatDateOnly(event.endDate)}
                    </Text>
                  </VStack>
                ) : (
                  <VStack align="start" spacing={1}>
                    <Text color="gray.600">
                      <strong>Início:</strong> {formatDateTime(event.startDate)}
                    </Text>
                    <Text color="gray.600">
                      <strong>Término:</strong> {formatDateTime(event.endDate)}
                    </Text>
                  </VStack>
                )}
              </Box>
            </Box>

            {/* Location */}
            {event.location && (
              <>
                <Divider />
                <Box>
                  <HStack mb={2}>
                    <Icon as={MdLocationOn} color="blue.500" />
                    <Text fontWeight="semibold" color="gray.700">Local</Text>
                  </HStack>
                  <Box pl={6}>
                    <Text color="gray.600">{event.location}</Text>
                  </Box>
                </Box>
              </>
            )}

            {/* Client */}
            {event.relatedCliente && (
              <>
                <Divider />
                <Box>
                  <HStack mb={2}>
                    <Icon as={FaBuilding} color="green.500" />
                    <Text fontWeight="semibold" color="gray.700">Cliente</Text>
                  </HStack>
                  <Box pl={6}>
                    {onClientClick ? (
                      <Button
                        variant="solid"
                        colorScheme="green"
                        size="sm"
                        onClick={() => onClientClick(event.relatedCliente)}
                      >
                        {event.relatedCliente.razaoSocial || event.relatedCliente.nomeFantasia || 'Cliente'}
                      </Button>
                    ) : (
                      <Text color="gray.600">
                        {event.relatedCliente.razaoSocial || event.relatedCliente.nomeFantasia || 'Cliente'}
                      </Text>
                    )}
                  </Box>
                </Box>
              </>
            )}

            {/* Participants */}
            {event.participants && event.participants.length > 0 && (
              <>
                <Divider />
                <Box>
                  <HStack mb={3}>
                    <Icon as={AtSignIcon} color="purple.500" />
                    <Text fontWeight="semibold" color="gray.700">
                      Participantes ({event.participants.length})
                    </Text>
                  </HStack>
                  <Box pl={6}>
                    <Wrap spacing={2}>
                      {event.participants.map((participant, index) => (
                        <WrapItem key={participant._id || index}>
                          <Tag size="lg" colorScheme="blue" borderRadius="full">
                            <TagLabel>
                              {participant.nomeCompleto || participant.name || participant.email || 'Participante'}
                            </TagLabel>
                          </Tag>
                        </WrapItem>
                      ))}
                    </Wrap>
                  </Box>
                </Box>
              </>
            )}

            {/* Reminders */}
            {event.reminders && event.reminders.length > 0 && (
              <>
                <Divider />
                <Box>
                  <Text fontWeight="semibold" color="gray.700" mb={2}>
                    Lembretes
                  </Text>
                  <VStack align="start" spacing={1} pl={6}>
                    {event.reminders.map((reminder, index) => (
                      <Text key={index} color="gray.600" fontSize="sm">
                        • {reminder.minutesBefore} minutos antes ({reminder.type})
                      </Text>
                    ))}
                  </VStack>
                </Box>
              </>
            )}

            {/* Created/Updated info */}
            {(event.createdAt || event.updatedAt) && (
              <>
                <Divider />
                <Box fontSize="sm" color="gray.500">
                  {event.createdAt && (
                    <Text>
                      Criado em: {new Date(event.createdAt).toLocaleString('pt-BR')}
                    </Text>
                  )}
                  {event.updatedAt && event.updatedAt !== event.createdAt && (
                    <Text>
                      Atualizado em: {new Date(event.updatedAt).toLocaleString('pt-BR')}
                    </Text>
                  )}
                </Box>
              </>
            )}
          </VStack>
        </ModalBody>

        <ModalFooter>
          <HStack spacing={3}>
            <Button variant="ghost" onClick={onClose}>
              Fechar
            </Button>
            <Button 
              colorScheme="blue" 
              leftIcon={<EditIcon />}
              onClick={onEdit}
            >
              Editar
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default EventViewModal;